import {
  RefObject,
  UIEventHandler,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';
import { useAppDispatch } from './useAppDispatcher.hook';
import { useAppSelector } from './useAppSelector.hook';
import {
  getAssistantHistoryHasMore,
  getAssistantHistoryLoadingMore,
  getAssistantIsLoading,
  getAssistantMessages,
} from '../redux/Assistant/Assistant.slice';
import { loadAssistantHistoryThunk } from '../redux/Assistant/Assistant.thunk';

const TOP_THRESHOLD = 32;

interface UseAssistantHistoryPaginationOptions {
  containerRef: RefObject<HTMLElement | null>;
  anchorRef?: RefObject<HTMLElement | null>;
  skipInitialScroll?: boolean;
}

export const useAssistantHistoryPagination = ({
  containerRef,
  anchorRef,
  skipInitialScroll = false,
}: UseAssistantHistoryPaginationOptions) => {
  const dispatch = useAppDispatch();
  const messages = useAppSelector(getAssistantMessages);
  const hasMore = useAppSelector(getAssistantHistoryHasMore);
  const loadingMore = useAppSelector(getAssistantHistoryLoadingMore);
  const isLoading = useAppSelector(getAssistantIsLoading);
  const previousFirstId = useRef<string>();
  const previousLastId = useRef<string>();
  const previousScrollHeight = useRef(0);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore) {
      return;
    }

    const container = containerRef.current;

    if (container) {
      previousScrollHeight.current = container.scrollHeight;
    }

    dispatch(loadAssistantHistoryThunk({ loadMore: true }));
  }, [containerRef, dispatch, hasMore, loadingMore]);

  const onScroll = useCallback<UIEventHandler<HTMLElement>>(
    event => {
      if (event.currentTarget.scrollTop <= TOP_THRESHOLD) {
        loadMore();
      }
    },
    [loadMore],
  );

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const firstId = messages[0]?.id;
    const lastId = messages[messages.length - 1]?.id;
    const prepended =
      Boolean(previousFirstId.current) && firstId !== previousFirstId.current;
    const appended =
      Boolean(previousLastId.current) && lastId !== previousLastId.current;

    if (prepended) {
      container.scrollTop = container.scrollHeight - previousScrollHeight.current;
    } else if (isLoading) {
      container.scrollTop = container.scrollHeight;
    } else if (appended || !previousLastId.current) {
      if (skipInitialScroll && !previousLastId.current && !appended) {
        // focus scroll handled by caller
      } else {
        const anchor = anchorRef?.current;

        if (anchor) {
          const paddingTop =
            Number.parseFloat(window.getComputedStyle(container).paddingTop) ||
            0;
          container.scrollTop = Math.max(
            0,
            container.scrollTop +
              (anchor.getBoundingClientRect().top -
                container.getBoundingClientRect().top) -
              paddingTop,
          );
        } else {
          container.scrollTop = container.scrollHeight;
        }
      }
    }

    previousFirstId.current = firstId;
    previousLastId.current = lastId;
    previousScrollHeight.current = container.scrollHeight;

    if (
      hasMore &&
      !loadingMore &&
      container.scrollHeight <= container.clientHeight + TOP_THRESHOLD
    ) {
      loadMore();
    }
  }, [
    anchorRef,
    containerRef,
    hasMore,
    isLoading,
    loadMore,
    loadingMore,
    messages,
    skipInitialScroll,
  ]);

  return { onScroll, loadingMore, hasMore };
};
